import { loadProfile, PersistentProfile } from './persistence';

const TELEMETRY_QUEUE_KEY = 'neon_maw_telemetry_queue';
const SESSION_QUEUE_KEY = 'neon_maw_session_queue';

function getQueue<T>(key: string): T[] {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
}

function saveQueue<T>(key: string, queue: T[]) {
  try {
    localStorage.setItem(key, JSON.stringify(queue.slice(-100))); // keep max 100
  } catch (e) {}
}

export const apiClient = {
  getProfileId() {
    let id = localStorage.getItem('neon_maw_profile_id');
    if (!id) {
      id = `p_${Date.now()}_${Math.floor(Math.random()*1000)}`;
      localStorage.setItem('neon_maw_profile_id', id);
    }
    return id;
  },

  async getDailySeed(): Promise<number> {
    try {
      const res = await fetch('/api/daily-seed');
      if (res.ok) {
        const data = await res.json();
        return data.seed;
      }
    } catch (e) {
      console.warn('API daily-seed failed, falling back to local');
    }
    return parseInt(new Date().toISOString().slice(0, 10).replaceAll('-', ''), 10);
  },

  async syncProfile(profile: PersistentProfile) {
    try {
      await fetch(`/api/profile/${this.getProfileId()}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: `Player_${this.getProfileId().slice(-4)}`, settings: profile })
      });
    } catch (e) {
      console.warn('API sync profile failed, will retry later');
    }
  },

  async startSession(): Promise<string> {
    try {
      const res = await fetch('/api/session/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profileId: this.getProfileId() })
      });
      if (res.ok) {
        const data = await res.json();
        return data.sessionId;
      }
    } catch (e) {
      console.warn('API startSession failed, falling back to local');
    }
    return `local_s_${Date.now()}`;
  },

  async finishSession(sessionId: string, score: number, seed: number, events: any[] = []) {
    const payload = { sessionId, profileId: this.getProfileId(), score, seed, events };
    try {
      const res = await fetch('/api/session/finish', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        this.flushQueues();
        return;
      }
    } catch (e) {
      console.warn('API finishSession failed, queueing offline');
    }
    const q = getQueue<any>(SESSION_QUEUE_KEY);
    q.push(payload);
    saveQueue(SESSION_QUEUE_KEY, q);
  },

  async getLeaderboard(seed?: number) {
    try {
      const url = seed ? `/api/leaderboard?seed=${seed}` : '/api/leaderboard';
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        return data.entries;
      }
    } catch (e) {
      console.warn('API leaderboard failed');
    }
    return [];
  },

  async sendTelemetry(type: string, data: any, sessionId?: string) {
    const payload = { sessionId, type, data };
    try {
      const res = await fetch('/api/telemetry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.ok) return;
    } catch (e) {
      console.warn('API telemetry failed, queueing offline');
    }
    const q = getQueue<any>(TELEMETRY_QUEUE_KEY);
    q.push(payload);
    saveQueue(TELEMETRY_QUEUE_KEY, q);
  },

  async flushQueues() {
    const sessions = getQueue<any>(SESSION_QUEUE_KEY);
    if (sessions.length > 0) {
      const s = sessions.shift();
      try {
        const res = await fetch('/api/session/finish', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(s)
        });
        if (res.ok) {
          saveQueue(SESSION_QUEUE_KEY, sessions);
          // recurse if successful to clear all
          this.flushQueues();
          return;
        }
      } catch (e) {}
    }

    const telemetry = getQueue<any>(TELEMETRY_QUEUE_KEY);
    if (telemetry.length > 0) {
      const t = telemetry.shift();
      try {
        const res = await fetch('/api/telemetry', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(t)
        });
        if (res.ok) {
          saveQueue(TELEMETRY_QUEUE_KEY, telemetry);
          this.flushQueues();
        }
      } catch (e) {}
    }
  }
};
